import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { Apple, Laptop, Server } from "lucide-react";
import {
  useBuildInstaller,
  useInstallerTags,
} from "@/hooks/security/useEdrInstallers";
import type {
  InstallerBuild,
  InstallerChannel,
  InstallerPlatform,
} from "@/types/edr-installers";

const EXPIRY_STOPS = [30, 60, 90, 180, 365];

const PLATFORMS: { id: InstallerPlatform; label: string; icon: typeof Apple }[] = [
  { id: "macos", label: "macOS", icon: Apple },
  { id: "windows", label: "Windows", icon: Laptop },
  { id: "linux", label: "Linux", icon: Server },
];

export function BuildInstallerDialog({
  open,
  onOpenChange,
  onBuilt,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onBuilt: (b: InstallerBuild) => void;
}) {
  const tagsQ = useInstallerTags();
  const build = useBuildInstaller();
  const [tagId, setTagId] = useState<string>("");
  const [platform, setPlatform] = useState<InstallerPlatform>("macos");
  const [channel, setChannel] = useState<InstallerChannel>("stable");
  const [expiryIdx, setExpiryIdx] = useState<number>(2); // 90 days

  const tags = tagsQ.data?.kind === "ok" ? tagsQ.data.tags : [];
  const tagsUnavailable = tagsQ.data?.kind === "unavailable";

  useEffect(() => {
    if (!tagId && tags.length > 0) setTagId(tags[0].id);
  }, [tags, tagId]);

  const expiresInDays = EXPIRY_STOPS[expiryIdx];
  const expiryDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + expiresInDays);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, [expiresInDays]);

  const handleSubmit = async () => {
    if (!tagId) return;
    try {
      const result = await build.mutateAsync({
        tagId,
        platform,
        channel,
        expiresInDays,
      });
      onBuilt(result);
      onOpenChange(false);
    } catch {
      // toast handled by hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Build sensor installer</DialogTitle>
          <DialogDescription>
            Pre-baked signed package scoped to a tag. Distribute via MDM
            (Jamf, Kandji, Mosyle…) or run manually on the target host.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Tag</Label>
            <Select value={tagId} onValueChange={setTagId} disabled={tags.length === 0}>
              <SelectTrigger>
                <SelectValue placeholder={tagsUnavailable ? "Tag service offline" : "Select a tag"} />
              </SelectTrigger>
              <SelectContent>
                {tags.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {typeof t.deviceCount === "number" ? (
                      <span className="text-muted-foreground"> · {t.deviceCount} devices</span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tags.length === 0 && !tagsQ.isLoading ? (
              <p className="text-[11px] text-muted-foreground">
                {tagsUnavailable
                  ? "Tag service is offline. Try again shortly."
                  : "No tags available. Create one in console.smplify.com first."}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Platform</Label>
            <div className="grid grid-cols-3 gap-2">
              {PLATFORMS.map((p) => {
                const Icon = p.icon;
                const active = platform === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlatform(p.id)}
                    className={cn(
                      "flex flex-col items-center gap-1 rounded border px-3 py-3 text-xs transition-colors",
                      active
                        ? "border-primary bg-primary/5 text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground hover:bg-muted/50",
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Release channel</Label>
              <div className="flex rounded border border-border p-0.5">
                {(["stable", "beta"] as InstallerChannel[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setChannel(c)}
                    className={cn(
                      "flex-1 rounded-sm px-2 py-1 text-xs capitalize transition-colors",
                      channel === c
                        ? "bg-muted text-foreground font-medium"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                Token expires in <span className="text-foreground font-medium">{expiresInDays}d</span>
              </Label>
              <Slider
                min={0}
                max={EXPIRY_STOPS.length - 1}
                step={1}
                value={[expiryIdx]}
                onValueChange={(v) => setExpiryIdx(v[0] ?? 2)}
                className="pt-2"
              />
              <p className="text-[11px] text-muted-foreground">Expires {expiryDate}</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!tagId || build.isPending}>
            {build.isPending ? "Building…" : "Build installer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
